/**
 * Interface Cell Renderers
 *
 * Provides custom cell renderer components for monitored interface data tables.
 * Handles special rendering logic for Polling Status, Priority, and Speed columns.
 */

import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import Icon from '@material-ui/core/Icon';
import clsx from 'clsx';
import { get } from 'lodash';

import { columnValueGetter } from '../../../data-table/DataTableColumns';
import { INNER_CELL_VALUE_CLASS } from '../../../data-table/DataTableConstants';
import format, { FORMATTER_TYPES } from '../../../../utilities/FormattingUtilities';

/**
 * Renders Polling Status cell with device-disabled styling when applicable.
 *
 * Shows the polling status value with reduced opacity when the device is disabled.
 *
 * @param {Object} row - The row data object
 * @param {Object} column - The column configuration object
 * @returns {JSX.Element} Rendered polling status cell
 */
export const renderPollingStatus = (row, column) => {
    const value = columnValueGetter(row, column);
    const deviceDisabled = get(row, 'deviceDisabled');

    if (deviceDisabled) {
        return <div className={clsx(INNER_CELL_VALUE_CLASS, 'device-disabled-interface')}>{value}</div>;
    }

    return <div className={INNER_CELL_VALUE_CLASS}>{value}</div>;
};

/**
 * Gets tooltip text for polling status based on status value and device state.
 *
 * @param {Object} row - The row data object
 * @returns {string|null} Tooltip text or null
 */
export const getPollingStatusTooltip = (row) => {
    const status = get(row, 'pollingStatus');
    const tooltipMap = {
        'User-Enabled': i18n._(t`Manually Enabled by User`),
        'User-Disabled': i18n._(t`Manually Disabled by User`),
        'Auto-Enabled': i18n._(t`Auto Enabled by Interface Selection Setting`),
        'Auto-Disabled': i18n._(t`Auto Disabled by Interface Selection Setting`),
        'Auto-Applying': i18n._(t`To be Enabled/Disabled by Interface Selection Setting`),
    };
    let toolTipMsg = tooltipMap[status];

    const deviceDisabled = get(row, 'deviceDisabled');
    if (deviceDisabled) {
        toolTipMsg += i18n._(t`, parent device is disabled`);
    }

    return toolTipMsg || status;
};

/**
 * Renders Priority Status cell with WAN high priority icon when applicable.
 *
 * Shows an arrow_upward icon next to the priority status for WAN interfaces.
 *
 * @param {Object} row - The row data object
 * @param {Object} column - The column configuration object
 * @returns {JSX.Element} Rendered priority status cell
 */
export const renderPriorityStatus = (row, column) => {
    const value = columnValueGetter(row, column);
    const isWan = get(row, 'isWan');

    if (isWan) {
        return (
            <div className={INNER_CELL_VALUE_CLASS}>
                {value}
                <Icon
                    className="small-icon"
                    title={i18n._(t`WAN High Priority`)}
                    style={{ fontSize: '1rem', marginLeft: '4px' }}
                >
                    arrow_upward
                </Icon>
            </div>
        );
    }

    return <div className={INNER_CELL_VALUE_CLASS}>{value}</div>;
};

/**
 * Gets tooltip text for Priority Status with dynamic i18n key construction.
 *
 * Maps priority status values to their corresponding tooltip messages.
 * This follows the portal/ui pattern of dynamic i18n key construction.
 *
 * @param {Object} row - The row data object
 * @returns {string|null} Tooltip text or null
 */
export const getPriorityStatusTooltip = (row) => {
    const status = get(row, 'priorityStatus');

    if (!status) {
        return null;
    }

    // Map status values to tooltip messages
    // Following portal/ui pattern: "User-Enabled" -> tooltip for user-enabled priority
    const tooltipMap = {
        'High-User': i18n._(t`Manually set to High priority by User`),
        'Low-User': i18n._(t`Manually set to Low priority by User`),
        'High-Auto': i18n._(t`Automatically set to High priority by Interface Priority Setting`),
        'Low-Auto': i18n._(t`Automatically set to Low priority by Interface Priority Setting`),
        Applying_Auto: i18n._(t`To be set by Interface Priority Setting`),
    };

    return tooltipMap[status] || status;
};

/**
 * Renders Speed cell with bold styling when speed is manually configured.
 *
 * @param {Object} row - The row data object
 * @param {Object} column - The column configuration object
 * @returns {JSX.Element} Rendered speed cell
 */
export const renderSpeed = (row, column) => {
    const value = columnValueGetter(row, column);
    const configuredSpeed = get(row, 'configuredSpeed');

    const className = clsx(INNER_CELL_VALUE_CLASS, {
        'bold-text': configuredSpeed,
    });

    return <div className={className}>{value}</div>;
};

/**
 * Gets tooltip text for Speed with manual speed indicator.
 *
 * Shows "(Manual Speed)" suffix when the speed is manually configured.
 *
 * @param {Object} row - The row data object
 * @returns {string} Tooltip text
 */
export const getSpeedTooltip = (row) => {
    const configuredSpeed = get(row, 'configuredSpeed');
    const speed = get(row, 'displaySpeed');

    // Format the speed value using log formatter
    const speedText = format(speed, { type: FORMATTER_TYPES.LOG, decimals: 0 });

    if (configuredSpeed) {
        const manualSpeedLabel = i18n._(t`Manual Speed`);
        return `${speedText} (${manualSpeedLabel})`;
    }

    return speedText;
};
